import _ from "lodash";
import React, {
  ChangeEvent,
  CSSProperties,
  useCallback,
  useEffect,
  useState,
} from "react";
import clsx from "clsx";
import Select, { OptionTypeBase, StylesConfig } from "react-select";
import AsyncCreatableSelect from "react-select/async-creatable";
import AsyncSelect from "react-select/async";

import { makeStyles } from "@material-ui/styles";
import {
  Button,
  Grid,
  TextField,
  Theme,
  Menu,
  MenuItem,
  Tooltip,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import AddIcon from "@material-ui/icons/AddOutlined";

import { useSelector } from "react-redux";

import {
  AggUserFilter,
  AnyUserFilter,
  CompositeUserFilter,
  PropUserFilter,
  RootCompositeUserFilter,
  TimeFilter,
} from "./types";
import { EventDefsState } from "../reducers/events";
import {
  ClockAddIcon,
  RemoveClockIcon,
  TrashIcon,
} from "../components/icons/themes/windows10";

import { makeRequest } from "../util";

type PropOption = {
  value: string;
  label: string;
};

const promisePropertyNameOptions = async (inputValue: string) => {
  const { data } = await makeRequest<any>("autocomplete_user_prop_name", {
    prop_name: inputValue,
  });
  return data.map((option: string) => ({ value: option, label: option }));
};

const promisePropertyValueOptions = (propName: string) =>
  async function (inputValue: string) {
    const { data } = await makeRequest<any>("autocomplete_user_prop", {
      prop_name: propName,
      prop_value: inputValue,
    });
    return data.map((option: string) => ({ value: option, label: option }));
  };

const lightestBlue = "#F2F5F8";
const lighterBlue = "#CAD6E2";
const lightBlue = "#A3B8CC";
const mediumBlue = "#507091";
const darkBlue = "#395068";
const darkestBlue = "#18222D";

const selectHeight = 30;
type SelectStyleParams = {
  minWidth?: number;
  monospaced?: boolean;
};
type SelectStyleFn = (params: SelectStyleParams) => StylesConfig;

const selectStyles: SelectStyleFn = ({
  minWidth = 110,
  monospaced = false,
}) => ({
  option: (base, { isSelected, isFocused }) => ({
    ...base,
    fontSize: "12px",
    color: isSelected ? "white" : isFocused ? "white" : darkBlue,
    backgroundColor: isSelected ? darkBlue : isFocused ? mediumBlue : "white",
    cursor: "pointer",
    // height: selectHeight,
    fontFamily: monospaced ? "monospace" : "inherit",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    width: "100%",
    "&:hover": {
      overflow: "visible",
      whiteSpace: "normal",
      textOverflow: "normal",
      height: "auto",
    },
  }),
  valueContainer: (base, state) => ({
    ...base,
    height: selectHeight,
    padding: "0 6px",
    fontSize: "12px",
    fontFamily: monospaced ? "monospace" : "inherit",
  }),
  input: (provided, state) => ({
    ...provided,
    margin: "0px",
    fontSize: "12px",
  }),
  indicatorsContainer: (base, state) => ({
    ...base,
    height: selectHeight,
  }),
  control: (base, { menuIsOpen }) => ({
    ...base,
    minWidth: minWidth,
    height: selectHeight,
    minHeight: selectHeight,
    color: darkestBlue,
    borderColor: lightestBlue,
    backgroundColor: menuIsOpen ? lighterBlue : lightestBlue,
    cursor: "pointer",
    fontFamily: monospaced ? "monospace" : "inherit",
    outline: "none",

    "&:hover": {
      borderColor: darkBlue,
    },

    "&:focus": {
      borderColor: lighterBlue,
      outline: "none",
    },
  }),
  singleValue: (base, state) => ({
    ...base,
    color: darkestBlue,
    fontFamily: monospaced ? "monospace" : "inherit",
  }),
  placeholder: (base, state) => ({
    ...base,
    fontStyle: "italic",
    color: darkBlue,
  }),
  dropdownIndicator: (base) => ({
    ...base,
    color: lightestBlue,
    position: "relative",
    top: -1,
  }),
  clearIndicator: (base) => ({
    ...base,
    color: lightestBlue,
    position: "relative",
    top: -1,
    "&:hover": {
      color: mediumBlue,
    },
  }),
  indicatorSeparator: (base, state) => ({
    ...base,
    display: "none",
    backgroundColor: darkBlue,
  }),
});

const filterStyles = makeStyles((theme: Theme) => ({
  filter: {
    alignItems: "center",
  },
  closeIcon: {
    alignSelf: "center",
    "&:hover": {
      cursor: "pointer",
    },
    height: 16,
    width: 16,
  },
  bold: {
    fontWeight: 550,
  },
  select: {
    paddingRight: 10,
  },
  nestedFilter: {
    padding: theme.spacing(1),
  },
}));

interface BaseFilterProps<T> {
  index: number;
  compositeOpSelector?: JSX.Element;
  onUpdateFilter: (index: number, newFilter: T) => void;
  onDeleteFilter: (index: number) => void;
  filter: T;
}

type FilterProps = BaseFilterProps<AnyUserFilter>;

type AggFilterProps = BaseFilterProps<AggUserFilter>;

type PropFilterProps = BaseFilterProps<PropUserFilter>;

type CompositeFilterProps = BaseFilterProps<CompositeUserFilter>;

type RootCompositeFilterViewProps = {
  onUpdateFilter: (filter: RootCompositeUserFilter) => void;
  filter: RootCompositeUserFilter;
};

const StyledInput: React.FC<{
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  value: any;
  name: string;
  style?: CSSProperties;
  placeholder?: string;
  className?: string;
  inputMode?:
    | "none"
    | "text"
    | "tel"
    | "url"
    | "email"
    | "numeric"
    | "decimal"
    | "search";
}> = ({
  onChange,
  value,
  style = {},
  inputMode,
  name,
  placeholder = "",
  className,
}) => {
  return (
    <input
      className={clsx(
        "border-b rounded border-navy-200 group-hover:border-navy-400 bg-transparent cursor-pointer hover:bg-navy-100 py-2 px-2",
        className
      )}
      style={{ width: 75, height: 30, ...style }}
      name={name}
      onChange={onChange}
      value={value}
      inputMode={inputMode}
      placeholder={placeholder}
      autoComplete="off"
    />
  );
};

const NumberInput: React.FC<{
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  value: any;
  name: string;
  style?: CSSProperties;
  className?: string;
}> = ({ onChange, value, style = {}, name, className }) => {
  return (
    <StyledInput
      style={style}
      name={name}
      onChange={onChange}
      value={value}
      inputMode="numeric"
      className={className}
    />
  );
};

export const RootCompositeFilterView: React.FC<RootCompositeFilterViewProps> = ({
  filter,
  onUpdateFilter,
}) => {
  const onFilterTypeChanges = (type: "and" | "or") => {
    const newFilter = _.extend({}, filter, { op: type });
    onUpdateFilter(newFilter);
  };

  const onAddSubFilter = (filterType: string) => {
    const newList = filter.filters.slice();
    newList.push({
      type: "composite_filter",
      op: "and",
      filters: [defaultFilterForType(filterType)],
    });
    const newFilter = _.extend({}, filter, { filters: newList });
    onUpdateFilter(newFilter);
  };

  const onSubFilterChanges = (
    subIndex: number,
    subFilter: CompositeUserFilter
  ) => {
    const newList = filter.filters.slice();
    newList[subIndex] = subFilter;
    const newFilter = _.extend({}, filter, { filters: newList });
    onUpdateFilter(newFilter);
  };

  const onDeleteSubFilter = (subIndex: number) => {
    const newList = filter.filters.slice();
    newList.splice(subIndex, 1);
    const newFilter = _.extend({}, filter, { filters: newList });
    onUpdateFilter(newFilter);
  };

  const buttonText = (filter && filter.op) || "and/or";

  return (
    <>
      {filter.filters.map((subFilter, i) => {
        return (
          <Grid container key={i}>
            <Grid item xs={2}>
              {i === 0 ? (
                <b>All users who</b>
              ) : (
                <div className="flex items-center">
                  <CompositeOpSelector
                    op={filter.op || "and"}
                    onOpChanges={(op) => onFilterTypeChanges(op)}
                  />
                </div>
              )}
            </Grid>
            <Grid item xs={10}>
              <CompositeFilter
                filter={subFilter}
                index={i}
                onUpdateFilter={onSubFilterChanges}
                onDeleteFilter={onDeleteSubFilter}
              />
            </Grid>
          </Grid>
        );
      })}
      {filter.filters.length === 0 ? (
        <AddFilterButton
          buttonText={`Users who...`}
          onAddSubFilter={onAddSubFilter}
        />
      ) : (
        <AddFilterButton
          buttonText={`${buttonText}...`}
          onAddSubFilter={onAddSubFilter}
        />
      )}
    </>
  );
};

const Filter: React.FC<FilterProps> = (props) => {
  if (props.filter.type === "prop_filter") {
    return <PropFilter {...props} filter={props.filter} />;
  } else if (props.filter.type === "agg_filter") {
    return <AggFilter {...props} filter={props.filter} />;
  } else {
    return <CompositeFilter {...props} filter={props.filter} />;
  }
};

const CompositeFilter: React.FC<CompositeFilterProps> = ({
  filter,
  index,
  onUpdateFilter,
  onDeleteFilter,
}) => {
  const classes = filterStyles();

  const onFilterTypeChanges = (type: "and" | "or") => {
    const newFilter = _.extend({}, filter, { op: type });
    onUpdateFilter(index, newFilter);
  };

  const onSubFilterChanges = (subIndex: number, subFilter: AnyUserFilter) => {
    const newList = filter.filters.slice();
    newList[subIndex] = subFilter;
    const newFilter = _.extend({}, filter, { filters: newList });
    onUpdateFilter(index, newFilter);
  };

  const onAddSubFilter = (filterType: string) => {
    const newList = filter.filters.slice();
    newList.push(defaultFilterForType(filterType));
    const newFilter = _.extend({}, filter, { filters: newList });
    onUpdateFilter(index, newFilter);
  };

  const onDeleteSubFilter = (subIndex: number) => {
    const newList = filter.filters.slice();
    newList.splice(subIndex, 1);
    if (newList.length == 0) {
      return onDeleteFilter(index);
    }

    const newFilter = _.extend({}, filter, { filters: newList });
    onUpdateFilter(index, newFilter);
  };

  return (
    <div className="border-l-4 mb-4 border-gray-500 hover:border-navy-600">
      {filter.filters.map((subFilter, i) => {
        return (
          <Filter
            filter={subFilter}
            index={i}
            key={i}
            compositeOpSelector={
              <CompositeOpSelector
                op={filter.op || "or"}
                onOpChanges={(op) => onFilterTypeChanges(op)}
              />
            }
            onUpdateFilter={onSubFilterChanges}
            onDeleteFilter={onDeleteSubFilter}
          />
        );
      })}
      <div className="flex justify-end">
        <AddFilterButton
          buttonText="Add Condition"
          onAddSubFilter={onAddSubFilter}
        />
      </div>
    </div>
  );
};

const AggFilter: React.FC<AggFilterProps> = ({
  filter,
  index,
  compositeOpSelector,
  onUpdateFilter,
  onDeleteFilter,
}) => {
  const classes = filterStyles();
  const eventDefinitions = useSelector(
    (state: { eventDefs: EventDefsState }) => {
      return state.eventDefs.eventDefs;
    }
  );

  let eventDefOptions: OptionTypeBase[] = _.map(eventDefinitions, (def) => ({
    value: def.id,
    label: def.name,
  }));

  const selectedEventDefOption = _.find(
    eventDefOptions,
    (opt) => opt.value === filter.event_def_id
  );

  const handlePropertyChanges = (newProps: Partial<AggUserFilter>) => {
    const newFilter = _.extend({}, filter, newProps);
    onUpdateFilter(index, newFilter);
  };

  const onAddTimeFilter = () => {
    handlePropertyChanges({ time_filter: { num: 30, unit: "days" } });
  };

  const operators = [
    [">", ">"],
    ["<", "<"],
    [">=", "≥"],
    ["<=", "≤"],
    ["=", "="],
    ["!=", "≠"],
  ];

  let operatorOptions: OptionTypeBase[] = _.map(operators, ([op, label]) => ({
    value: op,
    label: label,
  }));

  return (
    <div className="mb-6 text-sm text-gray-700 hover:bg-navy-50 px-4 py-4 group">
      <Grid container spacing={1} className="flex items-center">
        <Grid className="flex justify-start-items-center" item xs={1}>
          {index !== 0 && compositeOpSelector}
        </Grid>
        <Grid className="flex justify-end items-center" item xs={2}>
          <i>performed</i>
        </Grid>
        <Grid className="flex justify-start" item xs={8}>
          <Select
            placeholder="Select an event"
            options={eventDefOptions}
            menuPortalTarget={document.body}
            onChange={(eventDefID?: any) =>
              handlePropertyChanges({ event_def_id: eventDefID.value })
            }
            value={selectedEventDefOption}
            classNamePrefix="react-select--cohort"
            styles={selectStyles({ minWidth: 180 })}
          />
        </Grid>
        <Grid className="flex flex-wrap justify-end" item xs={1}>
          <Tooltip title="Delete this filter">
            <div
              className="hover:bg-navy-200 p-1 rounded-full flex justify-center items-center"
              onClick={() => onDeleteFilter(index)}
            >
              <CloseIcon className={classes.closeIcon} />
            </div>
          </Tooltip>
        </Grid>
      </Grid>
      <Grid container spacing={1} className={classes.filter}>
        <Grid item className="flex justify-end" xs={3}>
          <i>with count</i>
        </Grid>
        <Grid
          item
          className="flex flex-nowrap justify-start items-center"
          xs={9}
        >
          <span className="mr-3">
            <Select
              className="text-gray-700"
              placeholder="Operation"
              menuPortalTarget={document.body}
              options={operatorOptions}
              onChange={(value?: any) =>
                handlePropertyChanges({ op: value.value })
              }
              value={operatorOptions.find(
                (option) => option.value === filter.op
              )}
              classNamePrefix="react-select--cohort"
              styles={selectStyles({ minWidth: 40, monospaced: true })}
            />
          </span>
          <span className="mr-3">
            <NumberInput
              name="num-times"
              onChange={(e) => {
                const value = e.target.value;
                if (value !== "") {
                  handlePropertyChanges({ val: +value });
                } else {
                  handlePropertyChanges({ val: null });
                }
              }}
              value={filter.val !== null ? filter.val : ""}
            />
          </span>
          <span className="mr-3 justify-center">times</span>
        </Grid>
      </Grid>
      {filter.time_filter && (
        <TimeFilterView
          filter={filter.time_filter}
          onUpdateFilter={(newFilter) =>
            handlePropertyChanges({ time_filter: newFilter })
          }
        />
      )}
      {!filter.time_filter && (
        <Grid container spacing={1} className={classes.filter}>
          <Grid className="flex justify-end items-center" item xs={3}>
            <Tooltip title="Add a time constraint to this filter">
              <div
                className="inline-block btn cursor-pointer p-2 rounded bg-transparent hover:bg-navy-100"
                onClick={onAddTimeFilter}
              >
                <ClockAddIcon
                  className={clsx(
                    classes.closeIcon,
                    "invisible group-hover:visible"
                  )}
                />
              </div>
            </Tooltip>
          </Grid>
        </Grid>
      )}
    </div>
  );
};

interface TimeFilterProps {
  filter: TimeFilter;
  onUpdateFilter: (filter: TimeFilter | null) => void;
}

const TimeFilterView: React.FC<TimeFilterProps> = ({
  filter,
  onUpdateFilter,
}) => {
  const classes = filterStyles();

  const handleFilterChanges = (newProps: Partial<TimeFilter>) => {
    const newFilter = _.extend({}, filter, newProps);
    onUpdateFilter(newFilter);
  };

  const operators = ["hours", "days", "weeks", "months", "years"];

  let operatorOptions: OptionTypeBase[] = _.map(operators, (op) => ({
    value: op,
    label: op,
  }));

  return (
    <Grid container spacing={1} className={classes.filter}>
      <Grid className="flex justify-end items-center" item xs={3}>
        <div
          className="inline-block btn cursor-pointer p-2 rounded bg-transparent mr-2 hover:bg-navy-100 invisible group-hover:visible"
          onClick={() => onUpdateFilter(null)}
        >
          <Tooltip title="Remove this time constraint">
            <RemoveClockIcon className={clsx(classes.closeIcon)} />
          </Tooltip>
        </div>
        <i>within the last</i>
      </Grid>
      <Grid item xs={2} className="flex">
        <NumberInput
          className="mr-3"
          name="num-time-units"
          onChange={(e) => {
            const value = e.target.value;
            if (value !== "") {
              handleFilterChanges({ num: +value });
            } else {
              handleFilterChanges({ num: null });
            }
          }}
          value={filter.num !== null ? filter.num : ""}
        />
        <Select
          placeholder="Select an event"
          options={operatorOptions}
          menuPortalTarget={document.body}
          onChange={(value?: any) => handleFilterChanges({ unit: value.value })}
          value={{ value: filter.unit, label: filter.unit }}
          classNamePrefix="react-select--cohort"
          styles={selectStyles({ minWidth: 90 })}
        />
      </Grid>
    </Grid>
  );
};

const PropFilter: React.FC<PropFilterProps> = ({
  filter,
  index,
  compositeOpSelector,
  onUpdateFilter,
  onDeleteFilter,
}) => {
  const classes = filterStyles();

  const existingPropNames: PropOption[] = [];
  const existingPropValues: PropOption[] = [];
  let chosenPropNameOption, chosenPropValueOption;

  if (filter.prop) {
    chosenPropNameOption = {
      label: filter.prop,
      value: filter.prop,
    };
    existingPropNames.push(chosenPropNameOption);
  }

  if (filter.val) {
    chosenPropValueOption = {
      value: filter.val,
      label: filter.val,
    };
    existingPropValues.push(chosenPropValueOption);
  }

  // Uncomment when we have autocomplete for user props
  const [defaultPropNameOptions, setDefaultPropNameOptions] = useState<
    PropOption[]
  >(existingPropNames);
  const [defaultPropValueOptions, setDefaultPropValueOptions] = useState<
    PropOption[]
  >(existingPropValues);

  const handlePropertyChanges = (newProps: Partial<PropUserFilter>) => {
    const newFilter = _.extend({}, filter, newProps);
    if (newFilter.op === "is_defined") {
      onUpdateFilter(index, _.omit(newFilter, "val"));
    } else {
      onUpdateFilter(index, newFilter);
    }
  };

  const operatorOptions = [
    { value: "=", op: "=", label: "=", negate: false },
    { value: "!=", op: "=", label: "≠", negate: true },
    {
      value: "is_defined",
      op: "is_defined",
      label: "Is Defined",
      negate: false,
    },
    {
      value: "!is_defined",
      op: "is_defined",
      label: "Is Not Defined",
      negate: true,
    },
  ];

  const operatorsWithoutValue = ["is_defined", "!is_defined"];

  const selectedOperatorOption = _.find(
    operatorOptions,
    (opt) => opt.op === filter.op && opt.negate === filter.negate
  );

  const loadPropertyValueOptions = useCallback(
    promisePropertyValueOptions(filter.prop || ""),
    [filter]
  );
  const loadDefaultPropValueOptions = async () => {
    let options = await loadPropertyValueOptions("");
    options = options.concat(existingPropValues);
    setDefaultPropValueOptions(options);
  };

  useEffect(() => {
    loadDefaultPropValueOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadDefaultPropNameOptions = async () => {
    let options = await promisePropertyNameOptions("");
    options = options.concat(existingPropNames);
    setDefaultPropNameOptions(options);
  };

  useEffect(() => {
    loadDefaultPropNameOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="mb-6 text-sm text-gray-700 hover:bg-navy-50 px-4 py-4 group">
      <Grid className={classes.filter} container spacing={1}>
        <Grid className="flex justify-start-items-center" item xs={1}>
          {index !== 0 && compositeOpSelector}
        </Grid>
        <Grid className="flex justify-end items-center" item xs={2}>
          <i>property</i>
        </Grid>
        <Grid
          item
          className="flex flex-nowrap justify-start items-center"
          xs={8}
        >
          <AsyncCreatableSelect
            cacheOptions
            placeholder="Property"
            name="prop-name"
            menuPortalTarget={document.body}
            defaultOptions={defaultPropNameOptions}
            onChange={(option) => {
              // Idk, I think react select has bad typing
              // @ts-ignore
              handlePropertyChanges({ prop: option && option.value });
            }}
            // value={filter.val || ""}
            loadOptions={promisePropertyNameOptions}
            classNamePrefix="react-select--cohort"
            styles={selectStyles({ minWidth: 180 })}
            value={chosenPropNameOption}
            isClearable={true}
          />

          <Select
            className="mx-4"
            placeholder="Operation"
            menuPortalTarget={document.body}
            options={operatorOptions}
            onChange={(opt?: any) =>
              handlePropertyChanges({ op: opt.op, negate: opt.negate })
            }
            value={selectedOperatorOption}
            classNamePrefix="react-select--cohort"
            styles={selectStyles({ minWidth: 125 })}
          />
          {!operatorsWithoutValue.includes(filter.op) && (
            // <StyledInput
            //   style={{ width: "auto", minWidth: 250 }}
            //   placeholder="Value"
            //   name="value"
            //   onChange={(e) => handlePropertyChanges({ val: e.target.value })}
            //   value={filter.val || ""}
            // />
            <AsyncCreatableSelect
              placeholder="Value"
              name="value"
              menuPortalTarget={document.body}
              onChange={(option) => {
                // Idk, I think react select has bad typing
                // @ts-ignore
                handlePropertyChanges({ val: option && option.value });
              }}
              // value={filter.val || ""}
              defaultOptions={defaultPropValueOptions}
              loadOptions={loadPropertyValueOptions}
              classNamePrefix="react-select--cohort"
              styles={selectStyles({ minWidth: 180 })}
              value={chosenPropValueOption}
              isClearable={true}
            />
          )}
        </Grid>
        <Grid className="flex justify-end" item xs={1}>
          <Tooltip title="Delete this filter">
            <div
              className="hover:bg-navy-200 p-1 rounded-full flex justify-center items-center"
              onClick={() => onDeleteFilter(index)}
            >
              <CloseIcon className={classes.closeIcon} />
            </div>
          </Tooltip>
        </Grid>
      </Grid>
    </div>
  );
};

const defaultFilterForType = (filterType: string): AnyUserFilter => {
  if (filterType === "agg_filter") {
    return {
      type: "agg_filter",
      op: ">=",
      fn: "count",
      val: 1,
      time_filter: null,
    };
  } else {
    // Default to prop filter.
    return { type: "prop_filter", prop: "", negate: false, op: "=" };
  }
};

const CompositeOpSelector: React.FC<{
  op: string;
  onOpChanges: (op: "and" | "or") => void;
}> = ({ op, onOpChanges }) => {
  const operators = ["or", "and"];

  const operatorOptions: OptionTypeBase[] = _.map(operators, (op) => ({
    value: op,
    label: op,
  }));

  return (
    <Select
      className="mr-4 group"
      options={operatorOptions}
      menuPortalTarget={document.body}
      onChange={(op?: any) => onOpChanges(op.value)}
      defaultValue={operatorOptions[0]}
      value={{ value: op, label: op }}
      classNamePrefix="react-select--cohort"
      styles={selectStyles({ minWidth: 90 })}
    />
  );
};

interface AddFilterButtonProps {
  onAddSubFilter: (filterType: string) => void;
  buttonText: string;
}

const AddFilterButton: React.FC<AddFilterButtonProps> = ({
  buttonText,
  onAddSubFilter,
}) => {
  const classes = filterStyles();

  const [anchorEl, setAnchorEl] = React.useState<Element | null>(null);

  const handleSelection = (selection: string) => {
    setAnchorEl(null);
    onAddSubFilter(selection);
  };

  return (
    <>
      <div
        className="text-xs bg-navy-100 text-navy-800 inline-block inline-flex items-center px-3 py-1 rounded cursor-pointer hover:bg-navy-200"
        onClick={(event) => setAnchorEl(event.currentTarget)}
      >
        <AddIcon className="mr-1" style={{ width: 16 }} />
        {buttonText}
      </div>
      <Menu
        anchorEl={anchorEl}
        keepMounted
        open={!!anchorEl}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem onClick={() => handleSelection("agg_filter")}>
          Performed an Event
        </MenuItem>
        <MenuItem onClick={() => handleSelection("prop_filter")}>
          Have user property
        </MenuItem>
      </Menu>
    </>
  );
};
